import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  getDiscussionsByProjectId,
  addDiscussion,
  updateDiscussion,
} from "../../services/discussionService";

const DiscussionManager = () => {
  const { id: projectId } = useParams();

  const [discussions, setDiscussions] = useState([]);
  const [editingItem, setEditingItem] = useState(null);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [estimatedAmount, setEstimatedAmount] = useState("");
  const [scopeType, setScopeType] = useState("extra");

  const [error, setError] = useState("");

  const fetchDiscussions = useCallback(async () => {
    try {
      const data = await getDiscussionsByProjectId(projectId);
      setDiscussions(data);
    } catch {
      setError("Failed to load discussions.");
    }
  }, [projectId]);

  useEffect(() => {
    fetchDiscussions();
  }, [fetchDiscussions]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      projectId,
      title,
      description,
      amount: scopeType === "extra" ? parseFloat(estimatedAmount) : 0,
      type: scopeType,
    };

    try {
      if (editingItem) {
        await updateDiscussion({ ...payload, id: editingItem.id });
      } else {
        await addDiscussion(payload);
      }

      resetForm();
      fetchDiscussions();
    } catch {
      setError("Failed to save discussion.");
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setTitle(item.title);
    setDescription(item.description);
    setEstimatedAmount(item.amount || "");
    setScopeType(item.type);
  };

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setEstimatedAmount("");
    setScopeType("extra");
    setEditingItem(null);
  };

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto pb-24">
      <h2 className="text-xl font-bold mb-4">
        Discussions – Project #{projectId}
      </h2>

      {error && <p className="text-red-500 mb-4">{error}</p>}

      {/* FORM */}
      <form
        onSubmit={handleSubmit}
        className="bg-white p-4 rounded-xl shadow mb-8 space-y-4"
      >
        <input
          type="text"
          placeholder="Discussion Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          className="w-full px-3 py-2 border rounded"
        />

        <textarea
          placeholder="Discussion Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          required
          className="w-full px-3 py-2 border rounded"
        />

        <select
          value={scopeType}
          onChange={(e) => setScopeType(e.target.value)}
          className="w-full px-3 py-2 border rounded"
        >
          <option value="extra">Extra Work (Billable)</option>
          <option value="included">Included in Scope</option>
        </select>

        {scopeType === "extra" && (
          <input
            type="number"
            placeholder="Estimated Amount"
            value={estimatedAmount}
            onChange={(e) => setEstimatedAmount(e.target.value)}
            className="w-full px-3 py-2 border rounded"
            required
          />
        )}

        <button
          type="submit"
          className="bg-blue-600 text-white px-4 py-2 rounded w-full md:w-auto"
        >
          {editingItem ? "Update Discussion" : "Add Discussion"}
        </button>
      </form>

      {/* ===== MOBILE CARDS ===== */}
      <div className="space-y-4 md:hidden">
        {discussions.map((d) => (
          <div
            key={d.id}
            className="bg-white rounded-xl shadow p-4 border"
          >
            <div className="flex justify-between items-start">
              <h3 className="font-semibold">{d.title}</h3>
              <button
                onClick={() => handleEdit(d)}
                className="text-blue-600 text-sm"
              >
                Edit
              </button>
            </div>

            <p className="text-sm text-gray-600 mt-1">
              Scope: <span className="capitalize">{d.type}</span>
            </p>

            {d.type === "extra" && (
              <p className="text-sm mt-1">
                Estimated: <strong>₹{d.amount}</strong>
              </p>
            )}

            <p className="text-sm mt-2">
              Status:{" "}
              {d.status === "approved" ? (
                <span className="text-green-600 font-semibold">
                  Approved
                </span>
              ) : (
                <span className="text-yellow-600 font-semibold">
                  Pending
                </span>
              )}
            </p>
          </div>
        ))}
      </div>

      {/* ===== DESKTOP TABLE ===== */}
      <div className="hidden md:block bg-white rounded shadow overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-2 text-left">Title</th>
              <th className="p-2 text-left">Scope</th>
              <th className="p-2 text-left">Estimated</th>
              <th className="p-2 text-left">Approval</th>
              <th className="p-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {discussions.map((d) => (
              <tr key={d.id} className="border-t">
                <td className="p-2">{d.title}</td>
                <td className="p-2 capitalize">{d.type}</td>
                <td className="p-2">
                  {d.type === "extra" ? `₹${d.amount}` : "-"}
                </td>
                <td className="p-2">
                  {d.status === "approved" ? (
                    <span className="text-green-600 font-semibold">
                      Approved
                    </span>
                  ) : (
                    <span className="text-yellow-600 font-semibold">
                      Pending
                    </span>
                  )}
                </td>
                <td className="p-2">
                  <button
                    onClick={() => handleEdit(d)}
                    className="text-blue-600 hover:underline"
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DiscussionManager;
